import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { AlertTriangle, Check, Loader2, RotateCcw } from "lucide-react"
import * as React from "react"

import { cn } from "@/shared/utils/index"

/* ─────────────────────────────────────────────
   Variant definitions (unchanged for basic usage)
   ───────────────────────────────────────────── */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 hover:cursor-pointer",
  {
    variants: {
      variant: {
        primary: "bg-primary hover:bg-primary-hover text-primary-foreground",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        ['destructive-ghost']: "bg-transparent text-destructive hover:bg-destructive/20",
        outline:
          "border border-secondary bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        trigger: 'border border-border bg-input/30 dark:hover:bg-input/50 text-sm text-muted-foreground',
      },
      size: {
        xs: "h-7 rounded-md px-2",
        sm: "h-8 rounded-md px-3",
        md: "h-9 rounded-md px-4 py-2",
        lg: "h-11 rounded-md px-8 py-3",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
)

/* ─────────────────────────────────────────────
   Status type — drives stateful feedback
   ───────────────────────────────────────────── */
export type ButtonStatus = "idle" | "loading" | "success" | "error"

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Current async status — drives loading/success/error visuals */
  status?: ButtonStatus
  /** Text shown during success — omit for icon-only */
  successText?: string
  /** Text shown during error — omit for icon-only */
  errorText?: string
  /** Called when error state is clicked (retry) */
  onRetry?: () => void
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      status = "idle",
      successText,
      errorText,
      onRetry,
      children,
      disabled,
      onClick,
      ...props
    },
    ref
  ) => {
    // When asChild is used or no status, render the simple version
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      )
    }

    const isStateful = status !== "idle"
    const isLoading = status === "loading"
    const isSuccess = status === "success"
    const isError = status === "error"

    // Handle click: if error state, fire onRetry; otherwise normal onClick
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isError && onRetry) {
        onRetry()
        return
      }
      if (isLoading || isSuccess) return
      onClick?.(e)
    }

    return (
      <button
        ref={ref}
        className={cn(
          buttonVariants({ variant, size }),
          // Stateful overrides
          "relative overflow-hidden select-none",
          "transition-all duration-300 ease-out",
          isLoading && "cursor-wait",
          isSuccess && [
            "bg-success text-success-foreground",
            "border-success hover:bg-success",
          ],
          isError && [
            "bg-destructive text-destructive-foreground",
            "border-destructive hover:bg-destructive/90",
            "cursor-pointer",
          ],
          className
        )}
        disabled={disabled || isLoading || isSuccess}
        onClick={handleClick}
        aria-busy={isLoading}
        aria-live="polite"
        {...props}
      >
        {/* ── Idle content ── */}
        <span
          className={cn(
            "inline-flex items-center gap-2 transition-all duration-200",
            isStateful && "opacity-0 scale-95 absolute"
          )}
        >
          {children}
        </span>

        {/* ── Loading state ── */}
        {isLoading && (
          <span className="inline-flex items-center gap-2 animate-fade-in">
            <Loader2
              className="animate-spin"
              size={16}
              style={{
                animationDuration: "0.7s",
              }}
            />
          </span>
        )}

        {/* ── Success state ── */}
        {isSuccess && (
          <span
            className="inline-flex items-center gap-1.5"
            style={{
              animation: "button-status-in 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
            }}
          >
            <Check size={16} strokeWidth={2.5} />
            {successText && <span className="text-xs font-medium">{successText}</span>}
          </span>
        )}

        {/* ── Error state ── */}
        {isError && (
          <span
            className="inline-flex items-center gap-1.5"
            style={{
              animation: "button-status-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
            }}
          >
            {onRetry ? (
              <>
                <RotateCcw size={14} strokeWidth={2.5} />
                <span className="text-sm font-medium">Retry</span>
              </>
            ) : (
              <>
                <AlertTriangle size={14} strokeWidth={2.5} />
                {errorText && <span className="text-sm font-medium">{errorText}</span>}
              </>
            )}
          </span>
        )}
      </button>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
